import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Pagination } from 'react-bootstrap';
import { excerpt } from '../utils/markdown';
import './blog.css';

const PAGE_SIZE = 9;
const FETCH_TIMEOUT_MS = 10000;

const SkeletonCard = () => (
  <Col md={6} lg={4}>
    <div className="blog-skel-card">
      <div className="blog-skel blog-skel-media" />
      <div className="blog-skel-body">
        <div className="blog-skel blog-skel-line w-40" />
        <div className="blog-skel blog-skel-line w-80" style={{ height: 18 }} />
        <div className="blog-skel blog-skel-line w-60" />
      </div>
    </div>
  </Col>
);

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    const fetchPosts = async () => {
      setLoading(true);
      setFailed(false);
      try {
        const params = new URLSearchParams({
          'pagination[page]': String(page),
          'pagination[pageSize]': String(PAGE_SIZE),
          'sort': 'Datetime:desc',
        });
        const res = await fetch(`/api/blogs?${params.toString()}`, { signal: controller.signal });
        const json = await res.json();
        if (cancelled) return;
        if (json.error || !res.ok) throw new Error(json.error || 'Request failed');
        setPosts(json.data || []);
        setPageCount(json.meta?.pagination?.pageCount || 1);
      } catch (err) {
        if (!cancelled) setFailed(true);
      } finally {
        clearTimeout(timeoutId);
        if (!cancelled) setLoading(false);
      }
    };

    fetchPosts();
    return () => { cancelled = true; clearTimeout(timeoutId); controller.abort(); };
  }, [page, retryTick]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <Container className="py-5">
        <Row className="g-4">
          {Array.from({ length: 6 }, (_, i) => <SkeletonCard key={i} />)}
        </Row>
      </Container>
    );
  }

  if (failed) {
    return (
      <Container>
        <div className="blog-fallback">
          <div className="blog-fallback-icon">🚧</div>
          <h3>Blog Coming Soon</h3>
          <p>We're putting the finishing touches on our blog. Please check back shortly.</p>
          <button className="btn-retry" onClick={() => setRetryTick((t) => t + 1)}>Try Again</button>
        </div>
      </Container>
    );
  }

  if (posts.length === 0) {
    return (
      <Container>
        <div className="blog-fallback">
          <div className="blog-fallback-icon">📝</div>
          <h3>No posts yet</h3>
          <p>We haven't published anything here yet — check back soon for fresh content.</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="g-4">
        {posts.map((post) => {
          const href = post.redirecturl || `/blog/${post.slug}`;
          const isExternal = !!post.redirecturl;
          const CardInner = (
            <div className="blog-card h-100">
              <div className="blog-card-media">
                {post.image ? (
                  <img src={post.image} alt={post.title} loading="lazy" />
                ) : (
                  <div className="blog-card-media empty">No image</div>
                )}
              </div>
              <div className="blog-card-body">
                {post.postType === 'newsletter' && <span className="blog-badge-soft">Newsletter</span>}
                <div className="blog-card-title">{post.title}</div>
                <div className="blog-card-excerpt">{excerpt(post.content, 110)}</div>
                <div className="blog-card-meta">
                  <span className="blog-card-date">{formatDate(post.datetime)}</span>
                  <span className="blog-card-cta">Read more {isExternal ? '↗' : '→'}</span>
                </div>
              </div>
            </div>
          );

          return (
            <Col key={post.id} md={6} lg={4}>
              {isExternal ? (
                <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}>
                  {CardInner}
                </a>
              ) : (
                <Link to={href} style={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}>
                  {CardInner}
                </Link>
              )}
            </Col>
          );
        })}
      </Row>

      {pageCount > 1 && (
        <div className="d-flex justify-content-center mt-5">
          <Pagination>
            <Pagination.Prev disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />
            {Array.from({ length: pageCount }, (_, idx) => idx + 1).map((n) => (
              <Pagination.Item key={n} active={n === page} onClick={() => setPage(n)}>
                {n}
              </Pagination.Item>
            ))}
            <Pagination.Next disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))} />
          </Pagination>
        </div>
      )}
    </Container>
  );
};

export default BlogList;
